import { ReactNode, useCallback, useEffect } from 'react';

import { PRESENT_SLIDE_ID } from './activity-builder-present.constants';
import { unZoomContent, zoomContent } from './activity-present.utils';
import { useActivityBuilderPresentContext } from './activity-builder-present.context';
import { ACTIVITY_BUILDER_SLIDE } from './activity-builder-present-skip-links';
import * as Styled from './activity-builder-present-slide.style';

export const ActivityBuilderPresentSlide = ({
  children,
  controlsBefore,
  controlsAfter,
  activityContent,
  noContentPadding,
  currentStep,
  totalSteps,
  isContentLoading,
  isMediaUpMd,
  hasBanner,
  setIsSlideChanging,
}: {
  children: ReactNode;
  controlsBefore: ReactNode;
  controlsAfter: ReactNode;
  activityContent: ReactNode;
  noContentPadding?: boolean;
  currentStep: number;
  totalSteps: number;
  isContentLoading: boolean;
  isMediaUpMd: boolean;
  hasBanner?: boolean;
  setIsSlideChanging: (state: boolean) => void;
}) => {
  const extraSlidesToShow = totalSteps - currentStep;

  const { isZoomToFit, drawer } = useActivityBuilderPresentContext();

  const handleZoomResize = useCallback(() => {
    if (isZoomToFit) {
      zoomContent({ drawer, isMediaUpMd, hasBanner: !!hasBanner });
    } else {
      unZoomContent();
    }
    setTimeout(() => setIsSlideChanging(false), 300);
    // eslint-disable-next-line
  }, [
    isZoomToFit,
    hasBanner,
    drawer.isActive,
    drawer.isDetailActive,
    isMediaUpMd,
    setIsSlideChanging,
  ]);

  useEffect(() => {
    setIsSlideChanging(true);
    handleZoomResize();
  }, [isZoomToFit, setIsSlideChanging, handleZoomResize]);

  // listen to window size changes
  useEffect(() => {
    if (isZoomToFit && !isContentLoading) {
      handleZoomResize();
    }
    window.addEventListener('resize', handleZoomResize, false);
    return () => {
      window.removeEventListener('resize', handleZoomResize, false);
    };
  }, [isZoomToFit, isContentLoading, handleZoomResize]);

  return (
    <Styled.ActivityBuilderPresentSlideRow
      $isZoomToFit={isZoomToFit}
      $isLoading={isContentLoading}
      $hasBanner={hasBanner}
      id={ACTIVITY_BUILDER_SLIDE}
      role="region"
      tabIndex={-1}
    >
      <Styled.ActivityBuilderPresentSlideRowInner id={PRESENT_SLIDE_ID}>
        {Array.from({ length: extraSlidesToShow }, (_, index) => (
          <Styled.ActivityBuilderSlideExtra
            key={index}
            $index={index + 1}
            $length={extraSlidesToShow}
          />
        ))}
        {controlsBefore}
        <Styled.ActivityBuilderPresentSlide $zIndex={extraSlidesToShow}>
          {noContentPadding ? (
            children
          ) : (
            <Styled.ActivityBuilderSlideContent>
              {children}
            </Styled.ActivityBuilderSlideContent>
          )}
          {activityContent}
        </Styled.ActivityBuilderPresentSlide>
        {controlsAfter}
      </Styled.ActivityBuilderPresentSlideRowInner>
    </Styled.ActivityBuilderPresentSlideRow>
  );
};
